import { cropImageAPI } from '@/utils/api';

import { buildTaskIdCandidates } from './imageCropTaskIds';

import { applyImageCropResultCache } from './imageCropResultCache';

import {

  mapCropRowToDetailRow,

  normalizeCropPipeline,

  normalizeCropPosition,

  resolveCropUsedFallback,

} from './imageCropRowStatus';

import { rowHasCropResultTiles } from './imageCropDetailPrints';



function readFiniteNumber(value) {

  if (value == null) return null;

  const num = Number(value);

  return Number.isFinite(num) ? num : null;

}



function readCropPositionRaw(row) {

  if (!row || typeof row !== 'object') return null;

  return row.cropPosition ?? row.crop_position ?? null;

}



/** batch / 库记录是否含可展示的 cropPosition */

export function rowHasCropPosition(row) {

  const normalized = normalizeCropPosition(readCropPositionRaw(row));

  if (!normalized) return false;

  return Boolean(

    normalized.label

    || normalized.confidence != null

    || normalized.bboxNormalized

    || normalized.detectError,

  );

}



function cropRowRichness(row) {

  if (!row) return 0;

  let score = 0;

  if (pickCroppedUrlFromRow(row)) score += 8;

  if (rowHasCropPosition(row)) score += 4;

  if (normalizeCropPipeline(row)) score += 2;

  if (row.status) score += 1;

  return score;

}



/** 统一库记录字段（兼容 id / snake_case / 旧字段名；T3-4.2 保留 batch cropPosition） */

export function normalizeCropImageRow(row) {

  if (!row || typeof row !== 'object') return null;

  const imageId = row.imageId ?? row.image_id ?? row.id;

  if (imageId == null || String(imageId).trim() === '') return null;



  const cropPositionRaw = readCropPositionRaw(row);

  const pipeline = normalizeCropPipeline(row);

  const usedFallback = resolveCropUsedFallback(row, pipeline);

  const detectBatchIndex = readFiniteNumber(row.detectBatchIndex ?? row.detect_batch_index);



  return {

    ...row,

    imageId: String(imageId).trim(),

    originalImageUrl: String(

      row.originalImageUrl ?? row.original_image_url ?? row.originalUrl ?? '',

    ).trim(),

    originalImageUrlDisplay: String(

      row.originalImageUrlDisplay
      ?? row.original_image_url_display
      ?? row.originalUrlDisplay
      ?? row.original_display_url
      ?? '',

    ).trim(),

    croppedImageUrl: String(

      row.croppedImageUrl ?? row.cropped_image_url ?? row.croppedUrl ?? '',

    ).trim(),

    croppedDisplayUrl: pickCroppedDisplayUrlFromRow(row),

    status: row.status ?? row.crop_status ?? '',

    error: row.error ?? row.message ?? null,

    pipeline: pipeline || undefined,

    usedFallback,

    cropPosition: normalizeCropPosition(cropPositionRaw),

    ...(detectBatchIndex != null ? { detectBatchIndex } : {}),

  };

}



/** 是否有实质内容（排除 compat 空占位 { id: imageId }） */

export function isMeaningfulCropRecord(row) {

  const n = normalizeCropImageRow(row);

  if (!n) return false;

  const url = pickCroppedUrlFromRow(n);

  if (url) return true;

  if (rowHasCropPosition(n)) return true;

  const st = String(n.status || '').toLowerCase();

  return st === 'failed' || st === 'processing' || st === 'partial' || st === 'success';

}



/** 从单条聚合记录或数组展开为多张裁剪行 */

export function expandCropRecordsFromPayload(payload, imageIds = []) {

  const idSet = new Set((imageIds || []).map(String));

  const rows = [];



  const push = (raw) => {

    const n = normalizeCropImageRow(raw);

    if (!n || !isMeaningfulCropRecord(n)) return;

    if (idSet.size > 0 && !idSet.has(String(n.imageId))) return;

    rows.push(n);

  };



  if (payload == null) return rows;

  if (Array.isArray(payload)) {

    payload.forEach(push);

    return rows;

  }

  if (typeof payload !== 'object') return rows;



  if (Array.isArray(payload.images)) {

    payload.images.forEach(push);

    return rows;

  }

  if (Array.isArray(payload.data)) {

    payload.data.forEach(push);

    return rows;

  }

  if (Array.isArray(payload.records)) {

    payload.records.forEach(push);

    return rows;

  }

  if (payload.imageId != null || payload.croppedImageUrl || payload.cropped_image_url) {

    push(payload);

    return rows;

  }



  Object.entries(payload).forEach(([key, val]) => {

    if (!val || typeof val !== 'object' || Array.isArray(val)) return;

    if (['total', 'page', 'pageSize', 'message', 'data'].includes(key)) return;

    push({ ...val, imageId: val.imageId ?? val.id ?? key });

  });

  return rows;

}



/** T3-4.2：batch 行按 imageId 建 lookup，优先保留含 cropPosition / 裁剪 URL 的记录 */

export function buildCropRowLookup(batchRows = []) {

  const map = new Map();

  (batchRows || []).forEach((row) => {

    const normalized = normalizeCropImageRow(row);

    if (!normalized || !isMeaningfulCropRecord(normalized)) return;

    const key = String(normalized.imageId);

    const prev = map.get(key);

    if (!prev || cropRowRichness(normalized) >= cropRowRichness(prev)) {

      map.set(key, normalized);

    }

  });

  return map;

}



/**

 * T3-4.2：batch 响应（含 cropPosition）→ 详情页 detail row。

 * @param {Array<object>} batchRows

 * @param {string[]} imageIds

 * @param {{ getOriginalUrl?: (imageId: string, cropData?: object) => string }} [options]

 */

export function mapCropBatchRowsToDetailRows(batchRows, imageIds = [], { getOriginalUrl } = {}) {

  const lookup = buildCropRowLookup(batchRows);

  const ids = [...new Set((imageIds || []).filter(Boolean).map(String))];



  return ids.map((imageId) => {

    const cropData = lookup.get(imageId);

    const fromResolver = typeof getOriginalUrl === 'function'

      ? String(getOriginalUrl(imageId, cropData) || '').trim()

      : '';

    const originalUrl = fromResolver

      || String(cropData?.originalImageUrl || '').trim();

    return mapCropRowToDetailRow(imageId, cropData, originalUrl);

  });

}



/**

 * 异步解析原图 URL 后映射 batch → detail row（详情页加载 / poll 共用）。

 */

export async function resolveDetailRowsFromBatch(batchRows, imageIds = [], {

  getCachedOriginalUrl,

  resolveOriginalUrl,

  patchRow,

} = {}) {

  const lookup = buildCropRowLookup(batchRows);

  const ids = [...new Set((imageIds || []).filter(Boolean).map(String))];



  return Promise.all(ids.map(async (imageId) => {

    const cropData = lookup.get(imageId);

    let originalUrl = String(cropData?.originalImageUrl || '').trim();

    if (!originalUrl && typeof getCachedOriginalUrl === 'function') {

      originalUrl = String(getCachedOriginalUrl(imageId) || '').trim();

    }

    if (!originalUrl && typeof resolveOriginalUrl === 'function') {

      originalUrl = String(await resolveOriginalUrl(imageId, cropData) || '').trim();

    }



    let row = mapCropRowToDetailRow(imageId, cropData, originalUrl);

    if (typeof patchRow === 'function') {

      row = patchRow(row, cropData) || row;

    }

    return row;

  }));

}



function mergeRowsIntoMap(map, rows) {

  (rows || []).forEach((row) => {

    const n = normalizeCropImageRow(row);

    if (!n || !isMeaningfulCropRecord(n)) return;

    const key = String(n.imageId);

    const prev = map.get(key);

    if (!prev) {

      map.set(key, n);

      return;

    }

    if (cropRowRichness(n) >= cropRowRichness(prev)) {

      map.set(key, n);

    }

  });

}



/** 按任务 id 合并读取全部裁剪记录（多候选 id + 聚合旧数据） */

export async function loadCropImagesForTask(taskMeta, imageIds = []) {

  const ids = [...new Set((imageIds || []).filter(Boolean).map(String))];

  const candidates = buildTaskIdCandidates(taskMeta || {});

  const merged = new Map();



  for (const cid of candidates) {

    try {

      const { images } = await cropImageAPI.getByTask(cid, { imageIds: ids });

      mergeRowsIntoMap(merged, images);

    } catch {

      /* try next */

    }

    if (ids.length > 0 && merged.size >= ids.length) break;

    if (merged.size === 0) {

      try {

        const aggregate = await cropImageAPI.getTaskAggregate(cid);

        mergeRowsIntoMap(merged, expandCropRecordsFromPayload(aggregate, ids));

      } catch {

        /* ignore */

      }

    }

    if (ids.length > 0 && merged.size >= ids.length) break;

  }



  return Array.from(merged.values());

}



function readPrintCropUrls(row) {
  const prints = Array.isArray(row?.cropPosition?.prints) ? row.cropPosition.prints : [];
  const urls = [];
  prints.forEach((print) => {
    if (!print || typeof print !== 'object') return;
    const url = String(
      print.croppedImageUrl || print.cropped_image_url || print.croppedUrl || '',
    ).trim();
    if (url) urls.push(url);
  });
  return urls;
}

export function pickCroppedUrlFromRow(row) {

  if (!row) return '';

  const top = String(

    row.croppedImageUrl || row.cropped_image_url || row.croppedUrl || '',

  ).trim();

  if (top) return top;

  const fromPrints = readPrintCropUrls(row);

  return fromPrints[0] || '';

}

/** 原图 canonical（与后端 enrich 前字段对齐） */
export function pickOriginalUrlFromRow(row) {
  if (!row) return '';
  return String(
    row.originalImageUrl
    || row.original_image_url
    || row.originalUrl
    || '',
  ).trim();
}

/** 原图可展示 URL：优先后端 enrich 的 originalImageUrlDisplay（与裁剪效果一致） */
export function pickOriginalDisplayUrlFromRow(row) {
  if (!row) return '';
  return String(
    row.originalImageUrlDisplay
    || row.original_image_url_display
    || row.originalUrlDisplay
    || row.original_display_url
    || '',
  ).trim();
}



export function pickCroppedDisplayUrlFromRow(row) {

  if (!row) return '';

  const top = String(

    row.croppedImageUrlDisplay

    || row.cropped_image_url_display

    || row.croppedUrlDisplay

    || row.cropped_display_url

    || row.viewUrl

    || '',

  ).trim();

  if (top) return top;

  const prints = Array.isArray(row?.cropPosition?.prints) ? row.cropPosition.prints : [];

  for (const print of prints) {
    if (!print || typeof print !== 'object') continue;
    const display = String(
      print.croppedImageUrlDisplay
      || print.cropped_image_url_display
      || print.croppedDisplayUrl
      || print.viewUrl
      || print.croppedImageUrl
      || print.cropped_image_url
      || '',
    ).trim();
    if (display) return display;
  }

  return '';

}



function rowHasCroppedUrl(row) {

  return Boolean(pickCroppedUrlFromRow(row)) || rowHasCropResultTiles(row);

}



function mergeCropPrintsByIndex(prevPrints = [], nextPrints = []) {
  const map = new Map();

  const upsert = (print, fallbackIndex) => {
    if (!print || typeof print !== 'object') return;
    const printIndex = Number.isFinite(Number(print.printIndex ?? print.print_index))
      ? Number(print.printIndex ?? print.print_index)
      : fallbackIndex;
    const key = printIndex;
    const existing = map.get(key);
    const merged = existing ? { ...existing, ...print, printIndex } : { ...print, printIndex };
    const existingUrl = String(existing?.croppedImageUrl ?? existing?.cropped_image_url ?? '').trim();
    const nextUrl = String(print.croppedImageUrl ?? print.cropped_image_url ?? '').trim();
    if (existingUrl && !nextUrl) {
      merged.croppedImageUrl = existingUrl;
      merged.croppedImageUrlDisplay = existing.croppedImageUrlDisplay
        ?? existing.cropped_image_url_display
        ?? print.croppedImageUrlDisplay
        ?? print.cropped_image_url_display;
    }
    map.set(key, merged);
  };

  (prevPrints || []).forEach((print, index) => upsert(print, index));
  (nextPrints || []).forEach((print, index) => upsert(print, index));

  return Array.from(map.values()).sort((a, b) => Number(a.printIndex) - Number(b.printIndex));
}

function mergeCropPositionObjects(prevPosition, nextPosition, preferredPosition) {
  const base = preferredPosition && typeof preferredPosition === 'object'
    ? preferredPosition
    : (nextPosition && typeof nextPosition === 'object' ? nextPosition : prevPosition);
  if (!base || typeof base !== 'object') return base ?? null;

  const prevPrints = Array.isArray(prevPosition?.prints) ? prevPosition.prints : [];
  const nextPrints = Array.isArray(nextPosition?.prints) ? nextPosition.prints : [];
  if (prevPrints.length === 0 && nextPrints.length === 0) return base;

  const mergedPrints = mergeCropPrintsByIndex(prevPrints, nextPrints);
  return {
    ...base,
    prints: mergedPrints,
    printCount: mergedPrints.length,
    multiPrint: mergedPrints.length > 1,
  };
}

function mergeCropPositionFields(prev, next) {

  const prevScore = cropRowRichness(prev);

  const nextScore = cropRowRichness(next);

  const preferred = nextScore >= prevScore ? next : prev;

  const prevPosition = prev?.cropPosition;
  const nextPosition = next?.cropPosition;
  const preferredPosition = preferred?.cropPosition ?? prevPosition ?? nextPosition;

  return {

    cropPosition: mergeCropPositionObjects(prevPosition, nextPosition, preferredPosition),

    cropPositionDisplay: preferred.cropPositionDisplay ?? prev.cropPositionDisplay ?? next.cropPositionDisplay,

    pipeline: preferred.pipeline ?? prev.pipeline ?? next.pipeline,

    isQwenBbox: preferred.isQwenBbox ?? prev.isQwenBbox ?? next.isQwenBbox,

    usedFallback: preferred.usedFallback ?? prev.usedFallback ?? next.usedFallback,

  };

}



/**

 * 详情页从接口刷新时合并行数据：已成功展示的裁剪图不被后续轮询/同步盖回「等待处理」

 */

export function mergeDetailImageRows(prevRows = [], nextRows = [], taskId = null) {

  const prevMap = new Map((prevRows || []).map((row) => [String(row.imageId), row]));

  const merged = (nextRows || []).map((row) => {

    const key = String(row.imageId);

    const prev = prevMap.get(key);

    if (!prev) return row;

    const prevRevision = Number(prev.resultRevision) || 0;
    const nextRevision = Number(row.resultRevision) || 0;
    if (nextRevision > prevRevision) {
      return {
        ...row,
        originalUrl: row.originalUrl || prev.originalUrl,
        originalDisplayUrl: row.originalDisplayUrl || prev.originalDisplayUrl || '',
      };
    }
    if (prevRevision > nextRevision) {
      return {
        ...prev,
        originalUrl: row.originalUrl || prev.originalUrl,
        originalDisplayUrl: row.originalDisplayUrl || prev.originalDisplayUrl || '',
      };
    }

    const prevHasCrop = rowHasCroppedUrl(prev);

    const nextHasCrop = rowHasCroppedUrl(row);

    const positionFields = mergeCropPositionFields(prev, row);



    if (prev.status === 'success' && prevHasCrop && !nextHasCrop) {

      return {

        ...row,

        originalUrl: row.originalUrl || prev.originalUrl,

        originalDisplayUrl: row.originalDisplayUrl || prev.originalDisplayUrl || '',

        croppedUrl: prev.croppedUrl,

        croppedDisplayUrl: prev.croppedDisplayUrl || pickCroppedDisplayUrlFromRow(prev),

        status: 'success',

        error: prev.error,

        ...positionFields,

      };

    }



    if (prev.status === 'processing' && row.status === 'pending') {

      return {

        ...prev,

        originalUrl: row.originalUrl || prev.originalUrl,

        originalDisplayUrl: row.originalDisplayUrl || prev.originalDisplayUrl || '',

        ...positionFields,

      };

    }



    if (prev.status === 'failed' && row.status === 'pending' && prev.error) {

      return {

        ...prev,

        originalUrl: row.originalUrl || prev.originalUrl,

        originalDisplayUrl: row.originalDisplayUrl || prev.originalDisplayUrl || '',

        ...positionFields,

      };

    }



    const mergedRow = {

      ...prev,

      ...row,

      originalUrl: row.originalUrl || prev.originalUrl,

      originalDisplayUrl: row.originalDisplayUrl || prev.originalDisplayUrl || '',

      croppedUrl: nextHasCrop ? pickCroppedUrlFromRow(row) : (prevHasCrop ? prev.croppedUrl : ''),

      croppedDisplayUrl: nextHasCrop

        ? (pickCroppedDisplayUrlFromRow(row) || pickCroppedUrlFromRow(row))

        : (prev.croppedDisplayUrl || pickCroppedDisplayUrlFromRow(prev) || prev.croppedUrl || ''),

      status: nextHasCrop

        ? row.status

        : (prev.status === 'processing' ? 'processing' : prev.status),

      ...positionFields,

    };

    if (prevHasCrop && !nextHasCrop) {

      mergedRow.croppedUrl = prev.croppedUrl;

      mergedRow.croppedDisplayUrl = prev.croppedDisplayUrl || pickCroppedDisplayUrlFromRow(prev) || prev.croppedUrl;

      mergedRow.status = 'success';

    }

    return mergedRow;

  });

  return taskId ? applyImageCropResultCache(taskId, merged) : merged;

}

